/**
 * VCDN Processing Modal Manager
 * Handles the upload and processing of WhatsApp videos to VCDN
 */
export default class VcdnProcessing {
	#modal = null;
	#progressBar = null;
	#pollingInterval = null;
	#videoUuid = null;
	#redirectUrl = null;
	#whatsappId = null;
	#cancelled = false;

	constructor() {
		this.#modal = document.getElementById('vcdn-processing-modal');
		if (!this.#modal) {
			console.warn('VCDN processing modal not found in DOM');
			return;
		}
		this.#progressBar = this.#modal.querySelector('.progress-bar');
		this.#setupEventListeners();
	}

	#setupEventListeners() {
		const cancelBtn = this.#modal.querySelector('.vcdn-cancel-btn');
		if (cancelBtn) {
			cancelBtn.addEventListener('click', () => this.#cancel());
		}

		this.#modal.addEventListener('hidden.bs.modal', () => {
			this.#reset();
		});
	}

	/**
	 * Start the VCDN processing workflow
	 * @param {number} whatsappMessageId - The WhatsApp message ID
	 * @param {string} redirectUrl - URL to redirect to after processing (stories or multimedia)
	 * @param {string} actionType - 'story' or 'multimedia'
	 */
	async start(whatsappMessageId, redirectUrl, actionType = 'multimedia') {
		if (!this.#modal) {
			toastr.error('Modale di elaborazione non disponibile');
			return;
		}

		this.#whatsappId = whatsappMessageId;
		this.#redirectUrl = redirectUrl;
		this.#cancelled = false;

		this.#showModal();
		this.#setStep('upload', 'active');
		this.#updateProgress(10);

		try {
			const response = await this.#startVcdnUpload(whatsappMessageId);

			if (this.#cancelled) return;

			if (response.status !== 'ok') {
				throw new Error(response.message || 'Errore durante l\'upload');
			}

			this.#videoUuid = response.data.video_uuid;
			this.#setStep('upload', 'completed');
			this.#setStep('encoding', 'active');
			this.#updateProgress(40);

			this.#startPolling();
		} catch (error) {
			console.error('VCDN processing error:', error);
			this.#showError(error.message || 'Si è verificato un errore durante l\'elaborazione');
		}
	}

	async #startVcdnUpload(whatsappMessageId) {
		const url = window.appconfig?.routes?.whatsapp?.processVideo;
		if (!url) {
			throw new Error('Route per il processing video non configurata');
		}

		const response = await fetch(`${url}/${whatsappMessageId}`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'X-CSRF-TOKEN': window.appconfig?.csfr_token || '',
			},
		});

		if (!response.ok) {
			const errorData = await response.json().catch(() => ({}));
			throw new Error(errorData.message || `Errore HTTP: ${response.status}`);
		}

		return response.json();
	}

	#startPolling() {
		this.#pollingInterval = setInterval(async () => {
			if (this.#cancelled) {
				this.#stopPolling();
				return;
			}

			try {
				const status = await this.#checkVideoStatus();

				if (this.#cancelled) return;

				if (status.processing === false || status.status === 'processed') {
					this.#stopPolling();
					this.#setStep('encoding', 'completed');
					this.#setStep('complete', 'completed');
					this.#updateProgress(100);

					setTimeout(() => {
						this.#redirectToCreate();
					}, 1000);
				} else if (status.status === 'error') {
					this.#stopPolling();
					this.#showError('Errore durante l\'elaborazione del video');
				} else {
					const progress = this.#calculateProgress(status);
					this.#updateProgress(progress);
				}
			} catch (error) {
				console.error('Polling error:', error);
			}
		}, 2000);
	}

	async #checkVideoStatus() {
		const url = window.appconfig?.routes?.vcdn?.status;
		if (!url || !this.#videoUuid) {
			throw new Error('Configurazione VCDN mancante');
		}

		const response = await fetch(`${url}/${this.#videoUuid}`);
		if (!response.ok) {
			throw new Error(`Errore HTTP: ${response.status}`);
		}

		const data = await response.json();
		return data.data || data;
	}

	#calculateProgress(status) {
		const statusProgress = {
			uploaded: 50,
			processing: 65,
			active: 80,
			processed: 100,
		};

		return statusProgress[status.status] || 50;
	}

	#stopPolling() {
		if (this.#pollingInterval) {
			clearInterval(this.#pollingInterval);
			this.#pollingInterval = null;
		}
	}

	#redirectToCreate() {
		if (!this.#redirectUrl || !this.#videoUuid) return;

		const separator = this.#redirectUrl.includes('?') ? '&' : '?';
		const finalUrl = `${this.#redirectUrl}${separator}vcdn_uuid=${this.#videoUuid}`;

		window.location.href = finalUrl;
	}

	#showModal() {
		const bsModal = new bootstrap.Modal(this.#modal);
		bsModal.show();
	}

	#hideModal() {
		const bsModal = bootstrap.Modal.getInstance(this.#modal);
		if (bsModal) {
			bsModal.hide();
		}
	}

	#setStep(stepName, state) {
		const step = this.#modal.querySelector(`.vcdn-step-${stepName}`);
		if (!step) return;

		step.classList.remove('active', 'completed', 'error');
		step.classList.add(state);

		const pendingIcon = step.querySelector('.vcdn-status-pending');
		const waitingIcon = step.querySelector('.vcdn-status-waiting');
		const doneIcon = step.querySelector('.vcdn-status-done');

		if (pendingIcon) pendingIcon.classList.toggle('d-none', state !== 'active');
		if (waitingIcon) waitingIcon.classList.toggle('d-none', state === 'active' || state === 'completed');
		if (doneIcon) doneIcon.classList.toggle('d-none', state !== 'completed');
	}

	#updateProgress(percent) {
		if (this.#progressBar) {
			this.#progressBar.style.width = `${percent}%`;
		}
	}

	#showError(message) {
		const errorContainer = this.#modal.querySelector('.vcdn-processing-error');
		const errorMessage = this.#modal.querySelector('.vcdn-error-message');

		if (errorContainer) {
			errorContainer.classList.remove('d-none');
		}
		if (errorMessage) {
			errorMessage.textContent = message;
		}

		this.#setStep('upload', 'error');
		this.#setStep('encoding', 'error');
		this.#progressBar?.classList.add('bg-danger');
	}

	#cancel() {
		this.#cancelled = true;
		this.#stopPolling();
	}

	#reset() {
		this.#stopPolling();
		this.#videoUuid = null;
		this.#redirectUrl = null;
		this.#whatsappId = null;
		this.#cancelled = false;

		['upload', 'encoding', 'complete'].forEach((step) => {
			const stepEl = this.#modal.querySelector(`.vcdn-step-${step}`);
			if (stepEl) {
				stepEl.classList.remove('active', 'completed', 'error');
				const pending = stepEl.querySelector('.vcdn-status-pending');
				const waiting = stepEl.querySelector('.vcdn-status-waiting');
				const done = stepEl.querySelector('.vcdn-status-done');
				if (pending) pending.classList.add('d-none');
				if (waiting) waiting.classList.remove('d-none');
				if (done) done.classList.add('d-none');
			}
		});

		const uploadStep = this.#modal.querySelector('.vcdn-step-upload .vcdn-status-pending');
		if (uploadStep) uploadStep.classList.remove('d-none');

		const waitingUpload = this.#modal.querySelector('.vcdn-step-upload .vcdn-status-waiting');
		if (waitingUpload) waitingUpload.classList.add('d-none');

		if (this.#progressBar) {
			this.#progressBar.style.width = '0%';
			this.#progressBar.classList.remove('bg-danger');
		}

		const errorContainer = this.#modal.querySelector('.vcdn-processing-error');
		if (errorContainer) {
			errorContainer.classList.add('d-none');
		}
	}
}

document.addEventListener('DOMContentLoaded', () => {
	window.VcdnProcessing = new VcdnProcessing();
});

document.addEventListener('click', (e) => {
	const target = e.target.closest('[data-vcdn-process="true"]');
	if (target) {
		e.preventDefault();
		e.stopPropagation();

		const whatsappId = target.dataset.whatsappId;
		const redirectUrl = target.dataset.redirectUrl;

		if (whatsappId && redirectUrl && window.VcdnProcessing) {
			window.VcdnProcessing.start(whatsappId, redirectUrl);
		} else {
			console.error('Missing data for VCDN processing', { whatsappId, redirectUrl });
			toastr.error('Configurazione mancante per l\'elaborazione video');
		}
	}
});
